'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useRoomContext, useParticipants, useLocalParticipant } from '@livekit/components-react';
import toast from 'react-hot-toast';
import { hasModeratorRole, ParticipantRole, resolveRoleFromMetadata } from './participantRoles';
import { ParticipantEvent, RemoteParticipant, RoomEvent } from 'livekit-client';
import { isTrustedHostSender, isTrustedModerationSender } from './dataChannelModeration';

interface HostControlContextType {
  isHost: boolean;
  isCoHost: boolean;
  hasModeratorPermissions: boolean;
  localRole: ParticipantRole;
  coHostIdentities: Set<string>;
  mutedParticipants: Set<string>;
  videoDisabledParticipants: Set<string>;
  muteParticipant: (participantId: string) => Promise<void>;
  disableVideoParticipant: (participantId: string) => Promise<void>;
  isParticipantMuted: (participantId: string) => boolean;
  isParticipantVideoDisabled: (participantId: string) => boolean;
  assignCoHost: (participantId: string) => Promise<boolean>;
  revokeCoHost: (participantId: string) => Promise<boolean>;
}

const HostControlContext = createContext<HostControlContextType | undefined>(undefined);

export function HostControlProvider({
  children,
  forceHost = false,
}: {
  children: React.ReactNode;
  forceHost?: boolean;
}) {
  const room = useRoomContext();
  const participants = useParticipants();
  const localParticipant = useLocalParticipant();

  const [mutedParticipants, setMutedParticipants] = useState<Set<string>>(new Set());
  const [videoDisabledParticipants, setVideoDisabledParticipants] = useState<Set<string>>(
    new Set(),
  );
  const [localMetadata, setLocalMetadata] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!room) return;

    const participant = room.localParticipant;
    const handleMetadataChanged = () => {
      setLocalMetadata(participant.metadata ?? null);
    };

    handleMetadataChanged();
    participant.on(ParticipantEvent.ParticipantMetadataChanged, handleMetadataChanged);

    return () => {
      participant.off(ParticipantEvent.ParticipantMetadataChanged, handleMetadataChanged);
    };
  }, [room]);

  const localRole = React.useMemo<ParticipantRole>(() => {
    if (forceHost) {
      return 'host';
    }
    const metadata = localMetadata ?? localParticipant.localParticipant?.metadata;
    return resolveRoleFromMetadata(metadata);
  }, [forceHost, localMetadata, localParticipant.localParticipant?.metadata]);

  const isHost = forceHost || localRole === 'host';
  const isCoHost = localRole === 'co-host';
  const hasModeratorPermissions = hasModeratorRole(localRole);

  const coHostIdentities = React.useMemo(() => {
    const identities = new Set<string>();

    participants.forEach((participant) => {
      const role = resolveRoleFromMetadata(participant.metadata);
      if (role === 'co-host') {
        identities.add(participant.identity);
      }
    });

    return identities;
  }, [participants]);

  const muteParticipant = useCallback(
    async (participantId: string) => {
      if (!room || !hasModeratorPermissions) return;

      try {
        // Send data channel message to mute participant
        const message = {
          type: 'host-control',
          action: 'mute',
          targetParticipant: participantId,
          timestamp: Date.now(),
        };

        await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true,
        });

        setMutedParticipants((prev) => new Set(prev).add(participantId));
      } catch (error) {
        console.error('Erro ao mutar participante:', error);
      }
    },
    [room, hasModeratorPermissions],
  );

  const disableVideoParticipant = useCallback(
    async (participantId: string) => {
      if (!room || !hasModeratorPermissions) return;

      try {
        // Send data channel message to disable video
        const message = {
          type: 'host-control',
          action: 'disable-video',
          targetParticipant: participantId,
          timestamp: Date.now(),
        };

        await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true,
        });

        setVideoDisabledParticipants((prev) => new Set(prev).add(participantId));
      } catch (error) {
        console.error('Erro ao desabilitar vídeo do participante:', error);
      }
    },
    [room, hasModeratorPermissions],
  );

  const isParticipantMuted = useCallback(
    (participantId: string) => {
      return mutedParticipants.has(participantId);
    },
    [mutedParticipants],
  );

  const isParticipantVideoDisabled = useCallback(
    (participantId: string) => {
      return videoDisabledParticipants.has(participantId);
    },
    [videoDisabledParticipants],
  );

  const updateLocalRoleMetadata = useCallback(
    async (role: ParticipantRole) => {
      if (!room?.localParticipant || room.state !== 'connected') return;

      try {
        const currentMetadata = room.localParticipant.metadata;
        let parsed: Record<string, any> = {};

        if (currentMetadata) {
          parsed = JSON.parse(currentMetadata);
        }

        parsed.role = role;

        if (role === 'host') {
          parsed.isHost = true;
          parsed.forceHost = true;
        } else {
          delete parsed.isHost;
          delete parsed.forceHost;
        }

        const serialized = JSON.stringify(parsed);

        if (serialized !== currentMetadata) {
          await room.localParticipant.setMetadata(serialized);
        }
      } catch (error) {
        console.error('Erro ao atualizar metadata do participante local:', error);
      }
    },
    [room],
  );

  React.useEffect(() => {
    if (!forceHost || !room) {
      return;
    }

    const applyHostMetadata = () => {
      updateLocalRoleMetadata('host').catch((error) => {
        console.error(
          'HostControlProvider: não foi possível reforçar metadata de anfitrião:',
          error,
        );
      });
    };

    if (room.state === 'connected') {
      applyHostMetadata();
      return;
    }

    const handleConnected = () => {
      applyHostMetadata();
      room.off(RoomEvent.Connected, handleConnected);
    };

    room.on(RoomEvent.Connected, handleConnected);

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [forceHost, room, updateLocalRoleMetadata]);

  React.useEffect(() => {
    if (!forceHost || !room) {
      return;
    }

    const metadata = localMetadata ?? room.localParticipant?.metadata ?? null;
    const currentRole = resolveRoleFromMetadata(metadata);

    if (currentRole !== 'host' && room.state === 'connected') {
      updateLocalRoleMetadata('host').catch((error) => {
        console.error(
          'HostControlProvider: não foi possível restabelecer metadata de anfitrião:',
          error,
        );
      });
    }
  }, [forceHost, localMetadata, room, updateLocalRoleMetadata]);

  const assignCoHost = useCallback(
    async (participantId: string) => {
      if (!room || !isHost) return false;

      try {
        const message = {
          type: 'role-management',
          action: 'assign-cohost',
          targetParticipant: participantId,
          initiator: room.localParticipant.identity,
          timestamp: Date.now(),
        };

        await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true,
        });

        return true;
      } catch (error) {
        console.error('Erro ao designar co-anfitrião:', error);
        toast.error('Não foi possível designar o co-anfitrião.');
        return false;
      }
    },
    [room, isHost],
  );

  const revokeCoHost = useCallback(
    async (participantId: string) => {
      if (!room || !isHost) return false;

      try {
        const message = {
          type: 'role-management',
          action: 'revoke-cohost',
          targetParticipant: participantId,
          initiator: room.localParticipant.identity,
          timestamp: Date.now(),
        };

        await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(message)), {
          reliable: true,
        });

        return true;
      } catch (error) {
        console.error('Erro ao remover co-anfitrião:', error);
        toast.error('Não foi possível remover o co-anfitrião.');
        return false;
      }
    },
    [room, isHost],
  );

  React.useEffect(() => {
    if (!room) return;

    const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));

        if (data.type === 'role-management') {
          if (!isTrustedHostSender(participant)) {
            console.warn(
              'HostControlProvider: ignorando role-management de remetente que não é anfitrião',
            );
            return;
          }

          const targetParticipant = data.targetParticipant;

          if (targetParticipant === room.localParticipant.identity) {
            if (data.action === 'assign-cohost') {
              const currentRole = resolveRoleFromMetadata(room.localParticipant.metadata);

              if (currentRole !== 'co-host') {
                toast.success('Você foi designado como Co-Anfitrião.');
              }

              updateLocalRoleMetadata('co-host').catch((error) => {
                console.error('Erro ao aplicar função de co-anfitrião:', error);
              });
            }

            if (data.action === 'revoke-cohost') {
              const currentRole = resolveRoleFromMetadata(room.localParticipant.metadata);

              if (currentRole === 'co-host') {
                toast('Você não é mais Co-Anfitrião.', { icon: 'ℹ️' });
              }

              updateLocalRoleMetadata('participant').catch((error) => {
                console.error('Erro ao remover função de co-anfitrião:', error);
              });
            }
          }

          return;
        }

        if (data.type === 'host-control') {
          if (!isTrustedModerationSender(participant)) {
            console.warn(
              'HostControlProvider: ignorando host-control de remetente sem permissão de moderação',
            );
            return;
          }

          const targetParticipant = data.targetParticipant;

          // Check if this message is for the local participant
          if (targetParticipant === room.localParticipant.identity) {
            switch (data.action) {
              case 'mute':
                room.localParticipant.setMicrophoneEnabled(false);
                setMutedParticipants((prev) => new Set(prev).add(room.localParticipant.identity));
                break;
              case 'unmute':
                room.localParticipant.setMicrophoneEnabled(true);
                setMutedParticipants((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(room.localParticipant.identity);
                  return newSet;
                });
                break;
              case 'disable-video':
                room.localParticipant.setCameraEnabled(false);
                setVideoDisabledParticipants((prev) =>
                  new Set(prev).add(room.localParticipant.identity),
                );
                break;
              case 'enable-video':
                room.localParticipant.setCameraEnabled(true);
                setVideoDisabledParticipants((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(room.localParticipant.identity);
                  return newSet;
                });
                break;
            }
          }
        }
      } catch (e) {
        console.error('Erro ao processar mensagem de controle de anfitrião:', e);
      }
    };

    room.on('dataReceived', handleDataReceived);

    // Listen for participant track changes to clear host control state
    const handleTrackEnabled = (publication: any, participant: any) => {
      if (publication.kind === 'audio' && publication.isMuted === false) {
        // Participant unmuted themselves - clear host mute state
        setMutedParticipants((prev) => {
          const newSet = new Set(prev);
          newSet.delete(participant.identity);
          return newSet;
        });
      }
      if (publication.kind === 'video' && publication.isMuted === false) {
        // Participant enabled video themselves - clear host video disable state
        setVideoDisabledParticipants((prev) => {
          const newSet = new Set(prev);
          newSet.delete(participant.identity);
          return newSet;
        });
      }
    };

    room.on('trackUnmuted', handleTrackEnabled);

    return () => {
      room.off('dataReceived', handleDataReceived);
      room.off('trackUnmuted', handleTrackEnabled);
    };
  }, [room, updateLocalRoleMetadata]);

  const value: HostControlContextType = {
    isHost,
    isCoHost,
    hasModeratorPermissions,
    localRole,
    coHostIdentities,
    mutedParticipants,
    videoDisabledParticipants,
    muteParticipant,
    disableVideoParticipant,
    isParticipantMuted,
    isParticipantVideoDisabled,
    assignCoHost,
    revokeCoHost,
  };

  return <HostControlContext.Provider value={value}>{children}</HostControlContext.Provider>;
}

export function useHostControl() {
  const context = useContext(HostControlContext);
  if (context === undefined) {
    throw new Error('useHostControl must be used within a HostControlProvider');
  }
  return context;
}
